/**
 * Ask Cannafax suggestion rules — one standard for every conversation surface.
 * Before the first answer: up to 4 starters (context first, then a dependable fallback).
 * After an answer: up to 2 follow-ups grounded in that answer plus 1 fallback, shown only
 * when the user opens "Ask a follow-up". Suggestions only fill the question; they never act.
 */
export type Suggestion = { text: string; id?: string };

export const STARTER_LIMIT = 4;
export const FOLLOW_UP_CONTEXT_LIMIT = 2;
export const FALLBACK_PROMPT = 'What should I focus on next?';

const ANSWER_FOLLOW_UPS: { test: RegExp; prompts: string[] }[] = [
  { test: /\bbuyers?\b/i, prompts: ['Which buyers are most likely to respond?', 'Help me write a message to them'] },
  { test: /\bmenus?\b/i, prompts: ['Help me share this menu', 'Who has viewed this menu recently?'] },
  { test: /\blistings?\b/i, prompts: ['Which listings need attention first?', 'What details are missing from these listings?'] },
  { test: /\bmessages?|repl(y|ied|ies)\b/i, prompts: ['Help me reply', 'What should I do after I hear back?'] },
  { test: /\bbuy requests?\b/i, prompts: ['Show the closest matches', 'Help me refine this Buy Request'] },
];

function dedupe(items: Suggestion[], limit: number): Suggestion[] {
  const seen = new Set<string>();
  const out: Suggestion[] = [];
  for (const s of items) {
    const k = s.text.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length === limit) break;
  }
  return out;
}

/** Starters for a fresh conversation: contextual first, the fallback always fits. */
export function pickStarters(contextual: Suggestion[]): Suggestion[] {
  const ctx = dedupe(contextual, STARTER_LIMIT - 1);
  return dedupe([...ctx, { text: FALLBACK_PROMPT }, ...contextual], STARTER_LIMIT);
}

/** Follow-ups after an answer: grounded in the answer, then context, then one fallback. */
export function pickFollowUps(latestAnswer: string, contextual: Suggestion[] = []): Suggestion[] {
  const fromAnswer: Suggestion[] = [];
  for (const rule of ANSWER_FOLLOW_UPS) {
    if (rule.test.test(latestAnswer)) fromAnswer.push(...rule.prompts.map((text) => ({ text })));
  }
  const ctx = dedupe([...fromAnswer, ...contextual], FOLLOW_UP_CONTEXT_LIMIT)
    .filter((s) => s.text.toLowerCase() !== FALLBACK_PROMPT.toLowerCase());
  return dedupe([...ctx, { text: FALLBACK_PROMPT }], FOLLOW_UP_CONTEXT_LIMIT + 1);
}

/** True once the conversation holds an assistant answer worth reading. */
export function hasAnswer(messages: { role: string; content: string }[]): boolean {
  return messages.some((m) => m.role === 'assistant' && m.content.trim().length > 0);
}
